import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Star, MapPin, ArrowRight, Minus, Plus } from "lucide-react";
import api from "@/lib/api";
import { inr, fmtDate, prettyDate } from "@/lib/format";
import Shell from "@/components/Shell";
import SearchWidget from "@/components/SearchWidget";

export default function HotelResults() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const city = params.get("city") || "";
  const checkin = params.get("checkin") || fmtDate(new Date());
  const guests = Number(params.get("guests") || 2);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nights, setNights] = useState(2);

  useEffect(() => {
    setLoading(true);
    api.get(`/hotels/search?city=${encodeURIComponent(city)}`).then((r) => setResults(r.data.results)).finally(() => setLoading(false));
  }, [city]);

  const checkoutDate = () => { const d = new Date(checkin); d.setDate(d.getDate() + nights); return fmtDate(d); };

  const select = (h) => navigate("/checkout", { state: {
    type: "hotel", item_id: h.id, title: h.name, image: h.image,
    amount: h.price_per_night * nights, currency: "inr",
    details: { city: h.city, checkin, checkout: checkoutDate(), nights, rooms: 1, guests, hotel: h.name },
    summary: [{ k: "Check-in", v: prettyDate(checkin) }, { k: "Check-out", v: prettyDate(checkoutDate()) }, { k: "Nights", v: nights }, { k: "Guests", v: guests }, { k: "Per night", v: inr(h.price_per_night) }],
    icon: "hotel",
  }});

  return (
    <Shell>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-8"><SearchWidget initialTab="hotels" /></section>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl text-foreground mb-1">Hotels {city ? `in ${city}` : ""}</h1>
            <p className="text-muted-foreground text-sm">Check-in {prettyDate(checkin)} · {guests} guest(s)</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Nights</span>
            <div className="flex items-center gap-3 rounded-lg border border-input h-10 px-3">
              <button onClick={() => setNights(Math.max(1, nights - 1))} data-testid="nights-minus"><Minus size={15} /></button>
              <span className="font-semibold w-4 text-center" data-testid="nights-count">{nights}</span>
              <button onClick={() => setNights(nights + 1)} data-testid="nights-plus"><Plus size={15} /></button>
            </div>
          </div>
        </div>
        {loading ? (
          <div className="grid gap-5 md:grid-cols-2">{[...Array(4)].map((_, i) => <div key={i} className="h-64 rounded-2xl bg-secondary/60 animate-pulse" />)}</div>
        ) : results.length === 0 ? (
          <p className="text-muted-foreground py-16 text-center">No hotels found in "{city}". Try Goa, Manali, Rishikesh, Leh, Jaipur or Chopta.</p>
        ) : (
          <div className="grid gap-5 md:grid-cols-2" data-testid="hotel-results">
            {results.map((h, i) => (
              <div key={h.id} className="rounded-2xl border border-primary/10 bg-card overflow-hidden flex flex-col" data-testid={`hotel-${i}`}>
                <div className="h-44 overflow-hidden"><img src={h.image} alt={h.name} className="w-full h-full object-cover" /></div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground flex items-center gap-1"><MapPin size={12} /> {h.area}, {h.city}</span>
                    <span className="text-xs font-semibold flex items-center gap-1 text-foreground"><Star size={12} className="fill-accent text-accent" /> {h.rating}</span>
                  </div>
                  <h3 className="font-semibold text-lg text-foreground mt-1">{h.name}</h3>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {h.amenities.slice(0, 3).map((a) => <span key={a} className="text-xs rounded-full bg-secondary px-2.5 py-0.5 text-foreground/70">{a}</span>)}
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-primary/10">
                    <div>
                      <span className="font-display text-2xl text-foreground">{inr(h.price_per_night * nights)}</span>
                      <span className="text-xs text-muted-foreground block">{inr(h.price_per_night)} × {nights} night(s)</span>
                    </div>
                    <button onClick={() => select(h)} data-testid={`select-hotel-${i}`} className="inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-foreground px-5 py-2.5 text-sm font-semibold hover:-translate-y-px transition-transform">Book <ArrowRight size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </Shell>
  );
}
